import { z } from 'zod';

import {
    CUSTOMS_PRODUCT_TYPE_VALUES,
    LINE_STATE_VALUES,
    MARKETPLACE_VALUES,
    ORDER_FILE_FORMAT_VALUES,
    ORDER_FILE_TYPE_VALUES,
    ORDER_PRODUCT_FILE_TYPE_VALUES,
    ORDER_STATUS_VALUES,
    ORDER_STATUS_WRITE_VALUES,
    ORDER_TYPE_VALUES,
    PAYMENT_METHOD_VALUES,
} from '../fieldEnums';
import {
    doc,
    documentedStringEnum,
    itemCountField,
    isoDateTimeField,
    moneyField,
    optionalDocumentedStringEnum,
    optionalIsoCodeField,
    statusTimestampField,
} from './openapiHelpers';

const customizationTypeEnum = documentedStringEnum(
    ['engraving', 'stamping', 'grinding'],
    'Product customization type on the order line.',
);

const orderProductFileSchema = z.object({
    id: doc(z.number().int().optional(), 'File ID when updating an existing attachment.'),
    type: documentedStringEnum(ORDER_PRODUCT_FILE_TYPE_VALUES, 'Attachment type for the order line.'),
    data: doc(z.string().optional(), 'Base64 file content. Required for new files.'),
    fileType: documentedStringEnum(ORDER_FILE_FORMAT_VALUES, 'File format for the attachment.'),
    fileName: doc(z.string(), 'Original file name.'),
    fileSize: doc(z.union([z.string(), z.number()]).optional(), 'File size in bytes (string or number).'),
    orderProductId: doc(z.number().int().optional(), 'Internal order product ID (read-only in responses).'),
    createdAt: doc(z.union([z.string(), z.date()]).nullish(), 'Upload timestamp from files.CREATED_AT.'),
    creatorRole: doc(
        z.enum(['skladon_employee', 'client']).nullish(),
        'Who uploaded the file (Skladon employee or client).',
    ),
}).strict();

const orderProductV21Schema = z.object({
    sku: doc(z.string().min(1), 'Product SKU on the order line.'),
    count: itemCountField(),
    productPrice: moneyField(),
    lot: doc(z.string().nullish(), 'Lot/batch to pick from, when applicable.'),
    customsProductType: optionalDocumentedStringEnum(
        CUSTOMS_PRODUCT_TYPE_VALUES,
        'Customs classification of the order line. If omitted, defaults to saleOfGoods.',
    ),
    lineState: optionalDocumentedStringEnum(
        LINE_STATE_VALUES,
        'Line state. E = enabled (active line), D = disabled (omitted lines on update are marked D). If omitted on create, the line is enabled (E).',
    ),
    customizationType: customizationTypeEnum.nullish(),
    customizationText: doc(z.string().nullish(), 'Customization text when customizationType is set.'),
    bcxCodes: doc(
        z.array(z.string()).nullish(),
        'BCX codes printed on this order line. An array of strings, or null when there are none.',
    ),
    eshopOrderProductId: doc(
        z.string().min(1),
        'Your own identifier of this order line — required on every line in v2.1. Must be unique within the order; Skladon matches lines by this value on PUT/PATCH. Keep it stable for the lifetime of the order.',
    ),
    orderProductFiles: doc(z.array(orderProductFileSchema).optional(), 'Files attached to this order line.'),
}).strict();

const orderProductV21ResponseSchema = orderProductV21Schema.extend({
    confirmed: doc(z.number().int().nullish(), 'Quantity confirmed during packing.'),
    missingCount: doc(z.number().int().nullish(), 'Quantity missing during packing.'),
    locationId: doc(z.union([z.string(), z.number()]).nullish(), 'Warehouse location ID for the line.'),
    serialNumbers: doc(z.array(z.string().nullish()).nullish(), 'Serial numbers assigned after packing.'),
});

const fileSchema = z.object({
    id: doc(z.number().int().optional(), 'File ID when updating an existing attachment.'),
    data: doc(z.string().optional(), 'Base64 file content. Required for new files.'),
    fileName: doc(z.string(), 'Original file name.'),
    type: documentedStringEnum(
        ORDER_FILE_TYPE_VALUES,
        'Order attachment type. invoice = included in package; shippingAddonLabel = printed on package; shipResponse/other on update.',
    ),
    fileType: documentedStringEnum(ORDER_FILE_FORMAT_VALUES, 'File format.'),
    fileSize: doc(z.string().optional(), 'File size.'),
    createdAt: doc(z.union([z.string(), z.date()]).nullish(), 'Upload timestamp from files.CREATED_AT.'),
    creatorRole: doc(
        z.enum(['skladon_employee', 'client']).nullish(),
        'Who uploaded the file (Skladon employee or client).',
    ),
}).strict();

const orderV21BodyFields = {
    invoiceDate: isoDateTimeField(),
    requiredExpeditionDate: doc(z.string().nullish(), 'Required expedition date (ISO datetime).'),
    invoiceNumber: doc(z.string().nullish(), 'Invoice number shown on documents.'),
    partnerOrderId: doc(z.string().nullish(), 'Partner or marketplace order reference.'),
    recipientName: doc(z.string().nullish(), 'Recipient full name.'),
    recipientAttentionName: doc(z.string().nullish(), 'Recipient attention / contact person.'),
    recipientEmail: doc(z.string().nullish(), 'Recipient e-mail.'),
    recipientPhone: doc(z.string().nullish(), 'Recipient phone number.'),
    recipientCompanyName: doc(z.string().nullish(), 'Recipient company name.'),
    recipientCompanyId: doc(z.string().nullish(), 'Recipient company ID (IČO).'),
    recipientTaxId: doc(z.string().nullish(), 'Recipient tax ID (DIČ).'),
    recipientStreet: doc(z.string().nullish(), 'Recipient street and house number.'),
    recipientAddressAdditionalInfo: doc(z.string().nullish(), 'Extra recipient address line (floor, door, etc.).'),
    recipientCity: doc(z.string().nullish(), 'Recipient city.'),
    recipientState: optionalIsoCodeField(
        2,
        'Recipient country (ISO 3166-1 alpha-2, exactly 2 letters, for example CZ).',
    ),
    recipientProvinceCode: doc(z.string().nullish(), 'Recipient province code when applicable.'),
    recipientPostCode: doc(z.string().nullish(), 'Recipient postal code.'),
    recipientGln: doc(z.string().nullish(), 'Recipient GLN (Global Location Number).'),
    billingName: doc(z.string().nullish(), 'Billing contact name.'),
    billingAttentionName: doc(z.string().nullish(), 'Billing attention / contact person.'),
    billingEmail: doc(z.string().nullish(), 'Billing e-mail.'),
    billingPhone: doc(z.string().nullish(), 'Billing phone number.'),
    billingCompanyName: doc(z.string().nullish(), 'Billing company name.'),
    billingCompanyId: doc(z.string().nullish(), 'Billing company ID (IČO).'),
    billingTaxId: doc(z.string().nullish(), 'Billing tax ID (DIČ).'),
    billingStreet: doc(z.string().nullish(), 'Billing street and house number.'),
    billingAddressAdditionalInfo: doc(z.string().nullish(), 'Extra billing address line.'),
    billingCity: doc(z.string().nullish(), 'Billing city.'),
    billingState: optionalIsoCodeField(
        2,
        'Billing country (ISO 3166-1 alpha-2, exactly 2 letters, for example CZ).',
    ),
    billingProvinceCode: doc(z.string().nullish(), 'Billing province code when applicable.'),
    billingPostCode: doc(z.string().nullish(), 'Billing postal code.'),
    billingGln: doc(z.string().nullish(), 'Billing GLN.'),
    shipper: doc(z.string().nullish(), 'Shipper code (see GET /shipper-service-codes).'),
    price: moneyField(),
    shippingPrice: moneyField(),
    currency: optionalIsoCodeField(
        3,
        'Order currency, ISO 4217 (exactly 3 letters, for example CZK). This is the currency of price and shippingPrice, not of cash on delivery — that is codCurrency. Empty string or null if unknown.',
    ),
    paid: doc(
        z.boolean().nullish(),
        'Whether the order is already paid. If omitted on create, the order is treated as not paid.',
    ),
    paymentMethod: optionalDocumentedStringEnum(
        PAYMENT_METHOD_VALUES,
        'Payment method. 0 = cash on delivery, 1 = card online, 2 = bank transfer.',
    ),
    shipperServiceCode: doc(z.string().nullish(), 'Delivery service code (see GET /shipper-service-codes).'),
    branchId: doc(
        z.string().nullish(),
        'Groups several B2B orders that are delivered together to one place, usually the eshop’s own store. Not the carrier pickup point — that is addressId.',
    ),
    codPrice: moneyField(),
    codCurrency: optionalIsoCodeField(
        3,
        'Currency of the cash-on-delivery amount (codPrice), ISO 4217, exactly 3 letters (for example CZK). Not the order currency — that is currency. Set it when paymentMethod is 0 (cash on delivery). Empty string or null when there is no cash on delivery.',
    ),
    codRefNumber: doc(z.string().nullish(), 'Cash-on-delivery reference number.'),
    message: doc(z.string().nullish(), 'Message for the carrier label.'),
    addressId: doc(
        z.string().nullish(),
        'Carrier pickup-point ID where the customer collects the order (for example a parcel shop). Not an address-book entry.',
    ),
    orderType: optionalDocumentedStringEnum(
        ORDER_TYPE_VALUES,
        'Order type. b2c/B2C = consumer, b2b/B2B = business; transfer = stock transfer (requires recipientCompanyId).',
    ),
    note: doc(z.string().nullish(), 'Note visible in Skladon UI.'),
    printFiles: doc(
        z.boolean().optional(),
        'Print the order file of type invoice during expedition. If omitted on create, the eshop default for printing invoices is used.',
    ),
    holdByQuality: doc(
        z.boolean().optional(),
        'If true, pick this order from quality-held stock (goods held in the quality warehouse). If omitted on create, defaults to false.',
    ),
    pickFromCustomsWarehouse: doc(
        z.boolean().optional(),
        'If true, pick goods from customs-warehouse stock. Not available for every eshop. If omitted on create, defaults to false.',
    ),
    disableShippingReorder: doc(
        z.boolean().optional(),
        'If true, standard shipping is not ordered and a shippingAddonLabel file is required. If omitted on create, defaults to false.',
    ),
    underSanction: doc(
        z.boolean().optional(),
        'If true, the order is marked as under sanction. Only for B2B orders. If omitted on create, defaults to false.',
    ),
    allowExpedition: doc(
        z.boolean().nullish(),
        'Whether the order may be shipped. If omitted on create, defaults to true.',
    ),
    printReturnLabel: doc(
        z.boolean().nullish(),
        'Print a return label with the outbound shipment. If omitted on create, the eshop return-label setting is used when return labels are enabled; otherwise false.',
    ),
    marketplace: optionalDocumentedStringEnum(MARKETPLACE_VALUES, 'Marketplace the order came from.'),
    eshopNote: doc(z.string().nullish(), 'Your internal note stored on the order.'),
    preorderReleaseAt: doc(z.string().nullish(), 'Preorder release date and time (same semantics as GWMS v2).'),
    orderProducts: doc(z.array(orderProductV21Schema).optional(), 'Order lines — each line needs a unique eshopOrderProductId.'),
    orderFiles: doc(z.array(fileSchema).optional(), 'Files attached to the whole order.'),
} as const;

/** Accepted on write for backward compatibility; ignored by GWMS (not mapped to Core, not in OpenAPI). */
const ignoredOrderPriorityField = {
    priority: z.number().nullish(),
} as const;

const checkUniqueEshopOrderProductIds = (
    data: { orderProducts?: { eshopOrderProductId?: string }[] | null },
    ctx: z.RefinementCtx,
): void => {
    const seen = new Map<string, number>();
    data.orderProducts?.forEach((product, index) => {
        const { eshopOrderProductId } = product;
        if (!eshopOrderProductId) {
            return;
        }
        const firstIndex = seen.get(eshopOrderProductId);
        if (firstIndex === undefined) {
            seen.set(eshopOrderProductId, index);
            return;
        }
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['orderProducts', index, 'eshopOrderProductId'],
            message: `eshopOrderProductId must be unique within the order, "${eshopOrderProductId}" is already`
                + ` used on orderProducts[${firstIndex}]`,
        });
    });
};

const createOrderStatusSchema = documentedStringEnum(
    ['preReceived', 'preOrder'],
    'Optional on create. If omitted on create, the order starts as preReceived, unless paymentMethod is 1 (card) or 2 (bank transfer) and paid is not true — then it starts as waitingForPayment. preOrder keeps the order from being picked until you release it.',
);

const orderStatusWriteDescription =
    'Optional status on PUT/PATCH (same values for both methods). Not every listed value is a valid '
    + 'change from the current status — an impossible transition returns 403. '
    + 'See the order status flow guide.';

const updateOrderStatusSchema = documentedStringEnum(
    ORDER_STATUS_WRITE_VALUES,
    orderStatusWriteDescription,
);

const patchOrderStatusSchema = documentedStringEnum(
    ORDER_STATUS_WRITE_VALUES,
    orderStatusWriteDescription,
);

export const createOrderV21Schema = z.object({
    eshopOrderId: doc(z.string().min(1), 'Your order identifier — unique within the eshop.'),
    ...orderV21BodyFields,
    orderType: documentedStringEnum(
        ORDER_TYPE_VALUES,
        'Order type. b2c/B2C = consumer, b2b/B2B = business; transfer = stock transfer (requires recipientCompanyId).',
    ),
    orderProducts: doc(
        z.array(orderProductV21Schema).min(1),
        'Order lines — at least one line. Each line needs a unique eshopOrderProductId.',
    ),
    ...ignoredOrderPriorityField,
    status: createOrderStatusSchema.optional(),
}).strict().superRefine(checkUniqueEshopOrderProductIds);

/** OpenAPI / docs only — write body without ignored compat fields. */
export const createOrderV21DocumentedSchema = z.object({
    eshopOrderId: doc(z.string().min(1), 'Your order identifier — unique within the eshop.'),
    ...orderV21BodyFields,
    orderType: documentedStringEnum(
        ORDER_TYPE_VALUES,
        'Order type. b2c/B2C = consumer, b2b/B2B = business; transfer = stock transfer (requires recipientCompanyId).',
    ),
    orderProducts: doc(
        z.array(orderProductV21Schema).min(1),
        'Order lines — at least one line. Each line needs a unique eshopOrderProductId.',
    ),
    status: createOrderStatusSchema.optional(),
}).strict().superRefine(checkUniqueEshopOrderProductIds);

export const updateOrderV21Schema = z.object({
    ...orderV21BodyFields,
    ...ignoredOrderPriorityField,
    status: updateOrderStatusSchema.optional(),
}).strict().superRefine(checkUniqueEshopOrderProductIds);

export const updateOrderV21DocumentedSchema = z.object({
    ...orderV21BodyFields,
    status: updateOrderStatusSchema.optional(),
}).strict().superRefine(checkUniqueEshopOrderProductIds);

export const patchOrderV21Schema = z.object({
    ...orderV21BodyFields,
    ...ignoredOrderPriorityField,
    status: patchOrderStatusSchema.optional(),
}).partial().strict().refine(
    (data) => Object.keys(data).length > 0,
    { message: 'At least one field must be provided' },
).superRefine(checkUniqueEshopOrderProductIds);

export const patchOrderV21DocumentedSchema = z.object({
    ...orderV21BodyFields,
    status: patchOrderStatusSchema.optional(),
}).partial().strict().refine(
    (data) => Object.keys(data).length > 0,
    { message: 'At least one field must be provided' },
).superRefine(checkUniqueEshopOrderProductIds);

const trackingUrlSchema = z.object({
    trackingNumber: doc(z.string(), 'Carrier tracking number.'),
    url: doc(z.string(), 'Public tracking URL.'),
}).strict();

const orderV21ResponseReadOnly = {
    id: doc(z.number().int().optional(), 'Internal Skladon order ID.'),
    trackingNumbers: doc(z.array(z.string()), 'Carrier tracking numbers (read-only).'),
    trackingUrls: doc(z.array(trackingUrlSchema), 'Tracking URLs per tracking number (read-only).'),
    returnTrackingUrls: doc(z.array(trackingUrlSchema), 'Return shipment tracking URLs (read-only).'),
    exchangeTrackingUrl: doc(z.string().nullish(), 'Exchange shipment tracking URL (read-only).'),
    timestamp: doc(z.string(), 'Last significant order update timestamp.'),
    deliveredAt: doc(
        z.string().nullish(),
        'Authoritative delivery timestamp — set when status becomes delivered and never cleared. Use this to detect past delivery, not status alone.',
    ),
    updatedAt: doc(z.string(), 'Last update timestamp.'),
    status: documentedStringEnum(
        ORDER_STATUS_VALUES,
        'Current order status.',
    ),
    parentOrderId: doc(z.number().int().nullish(), 'Parent order ID when this order was split/derived.'),
    parentEshopOrderId: doc(z.string().nullish(), 'Parent eshop order ID when this order was split/derived.'),
    returnTrackingNumber: doc(z.string().nullish(), 'Return shipment tracking number.'),
    exchangeTrackingNumber: doc(z.string().nullish(), 'Exchange shipment tracking number.'),
    orderBoxCount: doc(z.number().int().nullish(), 'Number of shipping boxes/packages.'),
    incorrectOrderMessage: doc(z.string().nullish(), 'Reason when order is in incorrect status.'),
    weight: doc(z.number().nullish(), 'Total shipment weight in kilograms once packed. JSON number, null before packing.'),
    waitingForPaymentAt: statusTimestampField('waitingForPayment'),
    productsNotAvailableAt: statusTimestampField('productsNotAvailable'),
    suspendedAt: statusTimestampField('suspended'),
    releaseFromSuspendedAt: isoDateTimeField('Timestamp when the order was released from suspended status.'),
    incorrectAt: statusTimestampField('incorrect'),
    waitingForShipmentAt: statusTimestampField('waitingForShipment'),
    shipmentOrderedAt: statusTimestampField('shipmentOrdered'),
    inProcessAt: statusTimestampField('inProcess'),
    packedAt: statusTimestampField('packed'),
    waitingForCarrierAt: statusTimestampField('waitingForCarrier'),
    shippedAt: statusTimestampField('shipped'),
    atPickupPointAt: statusTimestampField('atPickupPoint'),
    returningBackAt: statusTimestampField('returningBack'),
    returnedBackAt: statusTimestampField('returnedBack'),
    canceledAt: statusTimestampField('canceled'),
    preReceivedAt: statusTimestampField('preReceived'),
    slaDate: doc(
        z.string().nullish(),
        'Deadline by which Skladon has to hand the order over to the carrier.',
    ),
    scannedAt: doc(
        z.string().nullish(),
        'When the order was first scanned in the warehouse.',
    ),
    sourceSys: doc(z.string().nullish(), 'System the order originated from.'),
} as const;

export const orderV21ResponseSchema = z.object({
    eshopOrderId: doc(z.string(), 'Your order identifier.'),
    ...orderV21BodyFields,
    orderProducts: doc(
        z.array(orderProductV21ResponseSchema).optional(),
        'Order lines including read-only packing fields.',
    ),
    ...orderV21ResponseReadOnly,
}).strict();

export type CreateOrderV21 = z.infer<typeof createOrderV21Schema>;
export type UpdateOrderV21 = z.infer<typeof updateOrderV21Schema>;
export type PatchOrderV21 = z.infer<typeof patchOrderV21Schema>;
export type OrderV21 = z.infer<typeof orderV21ResponseSchema>;
